/**
 * Shared UI atoms for the Stores pages (Store Operating System, Phase 1).
 *
 * Kept local to the stores/ folder: a page header (olive eyebrow + title +
 * subtitle, matching Connect / CoAs), a top-left back link (ArrowLeft per the
 * UI standard), an access-denied card, an "unmapped account" card (a store
 * user whose email is not linked to a store yet), and a "backend not available
 * yet" placeholder for graceful degradation.
 *
 * All styling comes from the shared amp-* Tailwind tokens; no new design
 * language. See COCKPIT.md section 2.9.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Store } from 'lucide-react';
import type { ReactNode } from 'react';

export function StoresHeader({
  title,
  subtitle,
  actions,
  eyebrow = 'Stores',
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
          {eyebrow}
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h1>
        {subtitle && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
    >
      <ArrowLeft className="h-3 w-3" /> {label}
    </Link>
  );
}

export function AccessDeniedCard({ what = 'This tool' }: { what?: string }) {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        {what} is gated by the Stores access scope. Ask HQ or IT to grant you access.
      </p>
    </div>
  );
}

// Shown when a signed-in store user's email is not linked to a store yet (the
// scope resolver returned 403 with reason 'unmapped'). The account is valid;
// it simply has no store assignment. This is the friendly "contact HQ" state.
export function UnmappedCard() {
  return (
    <div className="mx-auto max-w-md rounded-lg border border-amp-line bg-amp-white p-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amp-cream-warm text-amp-olive dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark">
        <Store className="h-6 w-6" aria-hidden />
      </div>
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">
        Your account isn't linked to a store yet
      </h2>
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Contact HQ to get your account connected to your store. Once you're linked, your daily
        routine and store thread will show up here.
      </p>
    </div>
  );
}

// Quiet placeholder shown when a backend slice is not deployed yet (404/503).
export function NotAvailableCard({ what }: { what: string }) {
  return (
    <div className="rounded-lg border border-dashed border-amp-line bg-amp-cream-warm px-4 py-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">{what} is not available yet</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        This part of the Store Cockpit is still being set up. It will appear here once the backend
        is live.
      </p>
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white px-4 py-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm text-red-700 dark:text-red-300">Could not load: {message}</p>
    </div>
  );
}

// Control class strings shared across the stores pages. Larger touch targets
// than the desktop-dense Connect controls, because store managers use phones.
export const storeInputCls =
  'w-full border border-amp-line rounded-lg px-3 py-2.5 text-[15px] bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted focus:outline-none focus:ring-2 focus:ring-amp-olive/40';
export const storeLabelCls =
  'block text-[13px] font-medium text-amp-ink dark:text-amp-dark-ink mb-1.5';

// Full-width primary button, sized for thumbs.
export const storePrimaryBtnCls =
  'inline-flex w-full items-center justify-center gap-1.5 text-[15px] font-semibold border border-amp-ink bg-amp-ink text-amp-white rounded-lg px-4 py-3 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface';
export const storeSecondaryBtnCls =
  'inline-flex items-center justify-center gap-1.5 text-sm border border-amp-line rounded-lg px-3 py-2 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';

// Desktop-dense (HQ) button classes, matching the Connect look.
export const secondaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
export const primaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface';
export const inputCls =
  'w-full border border-amp-line rounded px-2.5 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';
export const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1';
